import React, { useCallback, useMemo, useRef } from 'react';
import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import { getPaymentState, PaymentState } from '@ordr-as/payment-service-types/epos';
import type { PaymentStateResponse } from '@ordr-as/payment-service-types/epos';
import { useToast } from '@ordr-as/umami-react/toast';
import PaymentToastContent, { COUNTDOWN_DURATION_MS } from '../components/PaymentToastContent';

export interface ActiveRefund {
  paymentReference: string;
  paymentMethodId: string;
  paymentMethodLabel: string;
  locationId: number;
}

interface ActiveRefundsState {
  refunds: ActiveRefund[];
  addRefund: (refund: ActiveRefund) => void;
  removeRefund: (paymentReference: string) => void;
}

export const useActiveRefundsStore = create<ActiveRefundsState>((set) => ({
  addRefund: (refund) =>
    set((state) => ({
      refunds: [...state.refunds, refund],
    })),
  refunds: [],
  removeRefund: (paymentReference) =>
    set((state) => ({
      refunds: state.refunds.filter((r) => r.paymentReference !== paymentReference),
    })),
}));

function isRefundResolved(response: PaymentStateResponse): boolean {
  if (
    response.latestState === PaymentState.FULLY_REFUNDED ||
    response.latestState === PaymentState.PARTIALLY_REFUNDED
  ) {
    return true;
  }
  // Refund failed — state returns to CAPTURED with REFUND_FAILED event code
  if (response.latestEventCode === 'REFUND_FAILED') {
    return true;
  }
  return false;
}

function isRefundSuccess(response: PaymentStateResponse): boolean {
  return (
    response.latestState === PaymentState.FULLY_REFUNDED || response.latestState === PaymentState.PARTIALLY_REFUNDED
  );
}

/**
 * Polls `getPaymentState` for all active refunds and resolves them
 * with a toast when the state reaches a terminal refund state.
 */
export function useActiveRefunds() {
  const refunds = useActiveRefundsStore((s) => s.refunds);
  const removeRefund = useActiveRefundsStore((s) => s.removeRefund);
  const toasts = useToast();

  const refundsRef = useRef(refunds);
  refundsRef.current = refunds;

  const paymentReferences = useMemo(
    () =>
      refunds
        .map((r) => r.paymentReference)
        .sort()
        .join(','),
    [refunds]
  );

  const handlePollResults = useCallback(
    (results: PromiseSettledResult<PaymentStateResponse>[]) => {
      const currentRefunds = refundsRef.current;
      results.forEach((result, index) => {
        if (result.status !== 'fulfilled') {
          return;
        }
        const refund = currentRefunds[index];
        if (!refund) {
          return;
        }

        const response = result.value;
        if (!isRefundResolved(response)) {
          return;
        }

        removeRefund(refund.paymentReference);

        const toastId = `refund-${refund.paymentReference}`;
        const success = isRefundSuccess(response);
        const push = success ? toasts.pushSuccess : toasts.pushError;
        push({
          duration: COUNTDOWN_DURATION_MS,
          id: toastId,
          message: (
            <PaymentToastContent
              label={refund.paymentMethodLabel}
              statusText={success ? 'Refund completed' : 'Refund failed'}
              showCountdown
              onDismiss={() => toasts.remove(toastId)}
            />
          ),
        });
        setTimeout(() => toasts.remove(toastId), COUNTDOWN_DURATION_MS);
      });
    },
    [removeRefund, toasts]
  );

  const enabled = refunds.length > 0;

  useQuery({
    enabled,
    queryFn: async () => {
      const currentRefunds = refundsRef.current;
      const results = await Promise.allSettled(
        currentRefunds.map((r) => getPaymentState(r.locationId, r.paymentReference))
      );
      handlePollResults(results);
      return null;
    },
    queryKey: ['active-refunds-poll', paymentReferences],
    refetchInterval: 5000,
  });

  return { enabled, refunds };
}

export function useHasActiveRefund(paymentMethodId: string): boolean {
  return useActiveRefundsStore(
    useCallback((s) => s.refunds.some((r) => r.paymentMethodId === paymentMethodId), [paymentMethodId])
  );
}
